import { state } from "./app-state";
import { MOBILE_BREAKPOINT } from "./constants";
import { renderIcons, ui } from "./dom";
import {
  beginSelectionComposition,
  createEmptySelectionDraft,
  inspectReaderSelection,
  stabilizeSelectionDraft,
  type SelectionDraft,
} from "./selection";
import type { QuoteAnchor } from "./types";

let selectionDraft: SelectionDraft = createEmptySelectionDraft();
let selectionFrame = 0;
let selectionGeneration = 0;

export function getSelectionDraft(): SelectionDraft {
  return selectionDraft;
}

export function composeSelectionDraft(): void {
  selectionDraft = beginSelectionComposition(selectionDraft);
}

export function hideSelectionAction(): void {
  selectionGeneration += 1;
  window.cancelAnimationFrame(selectionFrame);
  selectionFrame = 0;
  selectionDraft = createEmptySelectionDraft();
  state.quote = null;
  ui.selectionAction.hidden = true;
  ui.selectionCommentBtn.hidden = false;
  ui.selectionHint.hidden = true;
  ui.selectionHint.textContent = "";
  ui.selectionAction.style.removeProperty("left");
  ui.selectionAction.style.removeProperty("top");
}

function positionSelectionAction(rect?: DOMRect): void {
  if (window.innerWidth <= MOBILE_BREAKPOINT || !rect) {
    ui.selectionAction.style.removeProperty("left");
    ui.selectionAction.style.removeProperty("top");
    return;
  }

  const viewport = window.visualViewport;
  const viewportLeft = viewport?.offsetLeft || 0;
  const viewportTop = viewport?.offsetTop || 0;
  const viewportWidth = viewport?.width || window.innerWidth;
  const viewportHeight = viewport?.height || window.innerHeight;
  const margin = 12;
  const gap = 8;
  const width = ui.selectionAction.offsetWidth;
  const height = ui.selectionAction.offsetHeight;
  const minLeft = viewportLeft + margin;
  const maxLeft = viewportLeft + viewportWidth - width - margin;
  const left = Math.min(Math.max(rect.left + rect.width / 2 - width / 2, minLeft), maxLeft);
  let top = rect.top - height - gap;
  if (top < viewportTop + margin) top = rect.bottom + gap;
  top = Math.min(Math.max(top, viewportTop + margin), viewportTop + viewportHeight - height - margin);

  ui.selectionAction.style.left = `${Math.round(left)}px`;
  ui.selectionAction.style.top = `${Math.round(top)}px`;
}

export function showSelectionAction(anchor: QuoteAnchor, rect?: DOMRect): void {
  if (selectionDraft.phase !== "ready" || selectionDraft.anchor !== anchor) {
    selectionDraft = {
      phase: "ready",
      anchor,
      range: null,
      rect: rect || null,
      signature: `manual:${state.currentDocId}:${anchor.text}`,
    };
  }
  state.quote = anchor;
  ui.selectionCommentBtn.hidden = false;
  ui.selectionHint.hidden = true;
  ui.selectionCommentBtn.querySelector("span")!.textContent = rect ? "写意见" : "评论此段";
  ui.selectionAction.hidden = false;
  renderIcons();
  positionSelectionAction(rect);
}

function showSelectionHint(message: string, rect?: DOMRect): void {
  selectionDraft = createEmptySelectionDraft();
  state.quote = null;
  ui.selectionCommentBtn.hidden = true;
  ui.selectionHint.hidden = false;
  ui.selectionHint.textContent = message;
  ui.selectionAction.hidden = false;
  positionSelectionAction(rect);
}

function inspectStableSelection(generation: number): void {
  if (generation !== selectionGeneration || selectionDraft.phase === "composing") return;
  const inspection = inspectReaderSelection(ui.reader);
  if (inspection.kind === "idle" || inspection.kind === "outside") {
    hideSelectionAction();
    return;
  }
  if (inspection.kind === "invalid") {
    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    showSelectionHint(inspection.message, range ? DOMRect.fromRect(range.getBoundingClientRect()) : undefined);
    return;
  }

  selectionDraft = stabilizeSelectionDraft(selectionDraft, inspection.candidate);
  if (selectionDraft.phase === "ready" && selectionDraft.anchor && selectionDraft.rect) {
    showSelectionAction(selectionDraft.anchor, selectionDraft.rect);
    return;
  }
  selectionFrame = window.requestAnimationFrame(() => inspectStableSelection(generation));
}

export function scheduleSelectionInspection(): void {
  if (selectionDraft.phase === "composing") return;
  // 编辑态下停用划词批注（document 级监听仍在跑，必须在这里挡住）。
  if (state.editor) {
    hideSelectionAction();
    return;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    hideSelectionAction();
    return;
  }
  if (!state.session.authenticated || state.view !== "document" || ui.documentView.hidden) {
    hideSelectionAction();
    return;
  }
  selectionGeneration += 1;
  const generation = selectionGeneration;
  window.cancelAnimationFrame(selectionFrame);
  selectionFrame = window.requestAnimationFrame(() => inspectStableSelection(generation));
}
